import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatDate, API } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Plus, X, Upload, FileText, Trash2, Pencil, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const statusOptions = [
  { value: "open", label: "Açık" },
  { value: "submitted", label: "Teklif Verildi" },
  { value: "won", label: "Kazanıldı" },
  { value: "lost", label: "Kaybedildi" },
];
const statusColor = { open: "bg-[#F59E0B]", submitted: "bg-[#0020C2]", won: "bg-[#16A34A]", lost: "bg-[#DC2626]" };

export default function TendersPage() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [form, setForm] = useState({});

  const emptyForm = () => ({ title: "", company_name: "", submission_date: "", deadline: "", bid_amount: 0, status: "open", notes: "" });

  const load = async () => {
    try { const { data } = await api.get("/tenders"); setItems(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ title: t.title, company_name: t.company_name, submission_date: t.submission_date,
             deadline: t.deadline, bid_amount: t.bid_amount, status: t.status, notes: t.notes || "" });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, bid_amount: Number(form.bid_amount) || 0 };
    try {
      if (editing) await api.put(`/tenders/${editing.id}`, payload);
      else await api.post("/tenders", payload);
      toast.success("Kaydedildi");
      setShowForm(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (t) => {
    if (!window.confirm("Bu ihaleyi silmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/tenders/${t.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const uploadPdf = async (tender, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/tenders/${tender.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dosya yüklendi");
      setUploadFor(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteFile = async (tender, fileId) => {
    if (!window.confirm("Dosyayı silmek istiyor musunuz?")) return;
    try { await api.delete(`/tenders/${tender.id}/files/${fileId}`); toast.success("Dosya silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const fileUrl = (tenderId, fileId) => {
    const token = localStorage.getItem("token");
    return `${API}/tenders/${tenderId}/files/${fileId}?auth=${token || ""}`;
  };

  return (
    <>
      <PageHeader
        title="İhaleler"
        subtitle="İhale takibi ve verdiğiniz teklif PDF'lerinin arşivi."
        testid="tenders-header"
        actions={
          <button data-testid="tenders-add-btn" onClick={openCreate} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors">
            <Plus className="w-4 h-4" strokeWidth={2.2} /> Yeni İhale
          </button>
        }
      />

      {items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] p-16 text-center">
          <FileText className="w-10 h-10 mx-auto text-neutral-300 mb-4" strokeWidth={1.2} />
          <div className="text-neutral-500 text-sm mb-4">Henüz ihale kaydı yok.</div>
          <button onClick={openCreate} className="text-xs uppercase tracking-widest font-semibold text-[#0020C2] hover:underline">İlk ihaleyi oluştur →</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className="bg-white border border-[#E5E5E5] p-5 rounded-md" data-testid={`tender-card-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${statusColor[t.status] || "bg-neutral-400"}`} />
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">
                      {statusOptions.find(s => s.value === t.status)?.label || t.status}
                    </span>
                  </div>
                  <div className="font-heading font-bold text-lg tracking-tight">{t.title}</div>
                  <div className="text-sm text-neutral-600 mt-0.5">{t.company_name}</div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`tender-edit-${t.id}`} onClick={() => openEdit(t)} className="p-1.5 hover:bg-[#F5F5F5]"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`tender-delete-${t.id}`} onClick={() => remove(t)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 py-3 border-y border-[#F5F5F5]">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Teklif Tarihi</div>
                  <div className="num text-sm font-semibold mt-1">{formatDate(t.submission_date)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Son Tarih</div>
                  <div className="num text-sm font-semibold mt-1 text-[#DC2626]">{formatDate(t.deadline)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Teklif Tutarı</div>
                  <div className="num text-sm font-semibold mt-1">{formatTL(t.bid_amount)}</div>
                </div>
              </div>

              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-500">Teklif Dosyaları ({t.files?.length || 0})</div>
                  <label className="cursor-pointer text-[11px] uppercase tracking-widest font-semibold text-[#0020C2] hover:underline flex items-center gap-1">
                    <Upload className="w-3.5 h-3.5" />
                    <span data-testid={`tender-upload-${t.id}`}>PDF Yükle</span>
                    <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPdf(t, f); e.target.value = ""; }} />
                  </label>
                </div>
                {(t.files || []).length === 0 ? (
                  <div className="text-xs text-neutral-400">Henüz dosya eklenmemiş.</div>
                ) : (
                  <div className="space-y-1.5">
                    {t.files.map((f) => (
                      <div key={f.file_id} className="flex items-center justify-between bg-[#FAFAFA] px-3 py-2 text-xs border border-[#F5F5F5]">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-3.5 h-3.5 text-neutral-500 shrink-0" />
                          <span className="truncate">{f.original_filename}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <a href={fileUrl(t.id, f.file_id)} target="_blank" rel="noreferrer" className="p-1 hover:bg-white"><ExternalLink className="w-3.5 h-3.5" /></a>
                          <button onClick={() => deleteFile(t, f.file_id)} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {t.notes && <div className="mt-3 text-xs text-neutral-600 border-t border-[#F5F5F5] pt-3">{t.notes}</div>}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{editing ? "Düzenle" : "Yeni"}</div>
                <div className="font-heading font-bold text-lg">İhale</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">İhale Başlığı *</label>
                <input data-testid="tender-field-title" required value={form.title || ""} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Firma *</label>
                <input data-testid="tender-field-company" required value={form.company_name || ""} onChange={(e) => setForm({...form, company_name: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Durum</label>
                <select data-testid="tender-field-status" value={form.status || "open"} onChange={(e) => setForm({...form, status: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                  {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Teklif Tarihi *</label>
                <input data-testid="tender-field-submission" type="date" required value={form.submission_date || ""} onChange={(e) => setForm({...form, submission_date: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Son Tarih *</label>
                <input data-testid="tender-field-deadline" type="date" required value={form.deadline || ""} onChange={(e) => setForm({...form, deadline: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Teklif Tutarı (₺)</label>
                <input data-testid="tender-field-amount" type="number" step="any" value={form.bid_amount || 0} onChange={(e) => setForm({...form, bid_amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Notlar</label>
                <textarea data-testid="tender-field-notes" rows={3} value={form.notes || ""} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="tender-submit-btn" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] transition-colors">{editing ? "Kaydet" : "Oluştur"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
